import React from 'react';
import styled from 'styled-components';
import { useLanguage } from '../context/LanguageContext';

// Styled components
const FooterContainer = styled.footer`
  background-color: ${props => props.theme.colors.backgroundDark};
  color: ${props => props.theme.colors.textLight};
  padding: 4rem 2rem 2rem;
`;

const FooterInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const FooterContent = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 3rem;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: 1fr 1fr;
    gap: 2rem;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const FooterLogo = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const LogoImage = styled.img`
  height: 50px;
  margin-right: 1rem;
`;

const LogoText = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: white;
`;

const FooterAbout = styled.p`
  font-size: 0.95rem;
  line-height: 1.6;
  margin-bottom: 1.5rem;
  opacity: 0.8;
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const SocialLink = styled.a`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: ${props => props.theme.colors.primary};
    transform: translateY(-3px);
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const FooterColumn = styled.div`
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    margin-bottom: 1rem;
  }
`;

const ColumnTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: white;
  margin-bottom: 1.5rem;
`;

const LinksList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const LinkItem = styled.li`
  margin-bottom: 0.75rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const FooterLink = styled.a`
  color: ${props => props.theme.colors.textLight};
  font-size: 0.95rem;
  text-decoration: none;
  transition: color 0.3s ease;
  display: inline-block;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    width: 0;
    height: 1px;
    bottom: -3px;
    left: 0;
    background-color: ${props => props.theme.colors.primary};
    transition: width 0.3s ease;
  }
  
  &:hover {
    color: white;
    
    &:after {
      width: 100%;
    }
  }
`;

const Divider = styled.hr`
  border: none;
  height: 1px;
  background-color: rgba(255, 255, 255, 0.1);
  margin: 3rem 0 2rem;
`;

const FooterBottom = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }
`;

const Copyright = styled.div`
  font-size: 0.9rem;
  opacity: 0.7;
`;

const BottomLinks = styled.div`
  display: flex;
  gap: 1.5rem;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const BottomLink = styled.a`
  font-size: 0.9rem;
  color: ${props => props.theme.colors.textLight};
  text-decoration: none;
  transition: color 0.3s ease;
  
  &:hover {
    color: white;
  }
`;

// Footer component
const Footer = () => {
  const { translate } = useLanguage();
  const currentYear = new Date().getFullYear();
  
  return (
    <FooterContainer>
      <FooterInner>
        <FooterContent>
          <FooterColumn>
            <FooterLogo>
              <LogoImage 
                src="/wt-logo-original.webp" 
                onError={(e) => { e.target.onerror = null; e.target.src = "/wt-logo-original.png"; }} 
                alt="WeatherTeller Logo" 
              />
              <LogoText>WeatherTeller</LogoText>
            </FooterLogo>
            <FooterAbout>
              {translate('footer.about', 'WeatherTeller is a community-driven weather reporting platform that enables users to share real-time weather conditions through an interactive map interface.')}
            </FooterAbout>
            <SocialLinks>
              {/* Only keeping GitHub as per user's request - not present in other media yet */}
              <SocialLink href="https://github.com/weatherteller" aria-label="GitHub">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22"></path>
                </svg>
              </SocialLink>
            </SocialLinks>
          </FooterColumn>
          
          <FooterColumn>
            <ColumnTitle>{translate('footer.navigation.title', 'Navigation')}</ColumnTitle>
            <LinksList>
              <LinkItem>
                <FooterLink href="/#features">{translate('footer.navigation.features', 'Features')}</FooterLink>
              </LinkItem>
              <LinkItem>
                <FooterLink href="/#data">{translate('footer.navigation.data', 'Weather Data')}</FooterLink>
              </LinkItem>
              <LinkItem>
                <FooterLink href="/#community">{translate('footer.navigation.community', 'Community')}</FooterLink>
              </LinkItem>
              <LinkItem>
                <FooterLink href="/#manual">{translate('footer.navigation.manual', 'User Manual')}</FooterLink>
              </LinkItem>
              <LinkItem>
                <FooterLink href="/#download">{translate('footer.navigation.download', 'Download')}</FooterLink>
              </LinkItem>
            </LinksList>
          </FooterColumn>
          
          <FooterColumn>
            <ColumnTitle>{translate('footer.resources.title', 'Resources')}</ColumnTitle>
            <LinksList>
              <LinkItem>
                <FooterLink href="https://blog.weatherteller.com">{translate('footer.resources.blog', 'Blog')}</FooterLink>
              </LinkItem>
              <LinkItem>
                <FooterLink href="https://forum.weatherteller.com">{translate('footer.resources.forum', 'Community Forum')}</FooterLink>
              </LinkItem>
              <LinkItem>
                <FooterLink href="https://api.weatherteller.com">{translate('footer.resources.developers', 'Developers')}</FooterLink>
              </LinkItem>
              <LinkItem>
                <FooterLink href="https://api.weatherteller.com/docs">{translate('footer.resources.api', 'API Documentation')}</FooterLink>
              </LinkItem>
              <LinkItem>
                <FooterLink href="https://app.weatherteller.com">{translate('footer.resources.app', 'WeatherTeller App')}</FooterLink>
              </LinkItem>
            </LinksList>
          </FooterColumn>
          
          <FooterColumn>
            <ColumnTitle>{translate('footer.support.title', 'Support')}</ColumnTitle>
            <LinksList>
              <LinkItem>
                <FooterLink href="#">{translate('footer.support.help', 'Help Center')}</FooterLink>
              </LinkItem>
              <LinkItem>
                <FooterLink href="#">{translate('footer.support.contact', 'Contact Us')}</FooterLink>
              </LinkItem>
              <LinkItem>
                <FooterLink href="#">{translate('footer.support.report', 'Report an Issue')}</FooterLink>
              </LinkItem>
              <LinkItem>
                <FooterLink href="#">{translate('footer.support.feedback', 'Feedback')}</FooterLink>
              </LinkItem>
              <LinkItem>
                <FooterLink href="#">{translate('footer.support.faq', 'FAQ')}</FooterLink>
              </LinkItem>
            </LinksList>
          </FooterColumn>
        </FooterContent>
        
        <Divider />
        
        <FooterBottom>
          <Copyright>
            © {currentYear} WeatherTeller. {translate('footer.copyright', 'All rights reserved.')}
          </Copyright>
          
          <BottomLinks>
            <BottomLink href="#">
              {translate('footer.legal.terms', 'Terms of Service')}
            </BottomLink>
            <BottomLink href="#">
              {translate('footer.legal.privacy', 'Privacy Policy')}
            </BottomLink>
            <BottomLink href="#">
              {translate('footer.legal.cookies', 'Cookie Policy')}
            </BottomLink>
          </BottomLinks>
        </FooterBottom>
      </FooterInner>
    </FooterContainer>
  );
};

export default Footer;